import { IconButton } from "@components/button";
import { OldImportClockIcon } from "@components/icon";
import { DateRangeSpecialValue } from "@components/inputs/date/popup/Calendar.utils";
import { isValidDate } from "@components/inputs/date/utils";
import { SwitchType } from "@components/inputs/switch/Switch";
import { getSimpleBoolSelectItems, IGetValueArgs, not, TGetValueFn, TValidatorFn } from "@components/smart/FieldInfo";
import { getAddressFields, getAddressGroup } from "@components/smart/GeneralFieldDefinition";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import {
    EntitySetName,
    EntityTypeName,
    IPrEmploymentEntity,
    PrBenefitEntity,
    PrEmployeeCarTaxabilityEntity,
    PrEmployeeEntity,
    PrEmploymentBenefitEntity,
    PrEmploymentCarEntity,
    PrEmploymentEntity,
    PrEmploymentExtraSalaryEntity,
    PrEmploymentInitialDataEntity,
    PrEmploymentInitialLongTermTimeBonusEntity,
    PrEmploymentInitialMonthDataEntity,
    PrEmploymentRetirementSavingTemporalCurrentEntity,
    PrEmploymentSalaryComponentEntity,
    PrEmploymentTemplateEntity,
    PrEmploymentTemporalCurrentEntity,
    PrEmploymentTemporalEntity,
    PrEmploymentWorkplaceAddressSelectionEntity,
    PrExtraSalaryEntity,
    PrExtraSalaryTypeEntity,
    PrSalaryComponentEntity,
    PrStatisticalDataEntity,
    PrSubmissionRegZecEntity,
    PrTimeBasedBonusTypeEntity,
    RegZecEmploymentEntity,
    WorkplaceTypeEntity
} from "@odata/GeneratedEntityTypes";
import {
    CountryCode,
    CsszEmploymentTypeCode,
    DetailedEmploymentSpecificationCode,
    PrEmploymentCategoryCode,
    PrEmploymentTypeCode,
    PrEmploymentWorkplaceAddressSelectionCode,
    PrSubmissionTypeCode,
    ShiftPatternCode,
    WorkplaceTypeCode,
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import {
    getTemporalAdditionalProperties,
    getTemporalPropertyFieldDefinition,
    getTemporalPropertyLineItemFieldDefinition
} from "@odata/TemporalUtils";
import { educationDef, getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import {
    getRetirementSavingsBasicFields,
    getRetirementSavingsComputationTypeDef,
    getRetirementSavingsIsIncludedAutomaticallyDef,
    getRetirementSavingsSecondaryFields
} from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import { getSubmissionTableDefinitionForFormTabs } from "@pages/payroll/submission/GetSubmissionTableDefinitionForFormTabs";
import { isNewEntity } from "@utils/definition.utils";
import { isDefined, isObjectEmpty } from "@utils/general";
import { capitalize } from "@utils/string";
import i18next from "i18next";
import React from "react";
import { ValidationError } from "yup";

import { BasicInputSizes, FieldType, GroupedField, Sort, ValidatorType } from "../../../enums";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath } from "../../../odata/BindingContext";
import DateType, { DATE_MAX, DateFormat, getUtcDayjs, TDateLike } from "../../../types/Date";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef, SECONDARY_FILTERS_ALL } from "../../../views/table/TableView.utils";
import { setDefByEntityType } from "../../getDefByEntityType";
import {
    getNumberOursSummaryDef,
    getNumberRangeFieldDefs,
    NumberRangeAdditionalProperties
} from "../../numberRange/NumberRange.utils";
import { getItemBreadCrumbsText, IDefinition, IGetDefinition, TFieldsDefinition } from "../../PageUtils";
import { getEmployeeFilterDef, getEmployeeSelectDef, getEmployeeTableColumnDefs } from "../employee/Employee.utils";
import {
    getCommonEmploymentDefs,
    getCommonEmploymentGroupDef,
    getRetirementSavingsTemporalDialogSetting
} from "../employmentTemplate/EmploymentTemplateDef";
import {
    detailedSpecificationDef,
    EmploymentStatus,
    EmploymentTranslationFiles,
    getEmploymentTypeDef,
    getFilterForTabId,
    isBeforeLastClosedPayrollPeriod,
    IsceDef,
    IscoDef,
    shiftPatternDef
} from "./Employment.utils";
import EmploymentFormView, { IEmploymentCustomData } from "./EmploymentFormView";
import {
    EmploymentInitialDataActionPath,
    hasCalculatedPayrollStartMonthConflict,
    hasInitialDataPayrollMonthConflict,
    isInitialDataRelevantEmployment
} from "./initialData/EmploymentInitialData.utils";

const hasNotOwnWorkplaceAddress: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const entity = storage.data.entity as IPrEmploymentEntity;
    const code = entity.WorkplaceAddressSelectionCode ?? entity.WorkplaceAddressSelection?.Code;
    return code !== PrEmploymentWorkplaceAddressSelectionCode.Own;
};

const sortedEmploymentTypes = [
    PrEmploymentTypeCode.Employment,
    PrEmploymentTypeCode.WorkAgreementDPP,
    PrEmploymentTypeCode.WorkAgreementDPC,
    PrEmploymentTypeCode.Compensation
];

const isDPP: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const typeCode = storage.getValueByPath(PrEmploymentEntity.TypeCode) as PrEmploymentTypeCode;
    return typeCode === PrEmploymentTypeCode.WorkAgreementDPP;
};

export const isStatutoryWithCompensation: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const categoryCode = storage.getValueByPath(PrEmploymentEntity.EmploymentCategoryCode) as PrEmploymentCategoryCode;
    const typeCode = storage.getValueByPath(PrEmploymentEntity.TypeCode) as PrEmploymentTypeCode;
    return categoryCode === PrEmploymentCategoryCode.Associate && typeCode === PrEmploymentTypeCode.Compensation;
};


const getFitToEmploymentDurationValidator = (granularity: "month" | "day"): TValidatorFn => {
    return (value, args, testContext) => {
        const { storage } = args;
        const current = value as TDateLike;
        const entity = storage.data.entity;
        const { DateStart, DateEnd } = entity;
        const date = getUtcDayjs(current);
        const skipValidation = !current || !isValidDate(current as Date);

        // if date start is set and later than the date calculate payslip start
        if (!skipValidation && (
            (DateStart && getUtcDayjs(DateStart).isAfter(date, granularity)) || (DateEnd && getUtcDayjs(DateEnd).isBefore(date, granularity)))) {
            return new ValidationError(i18next.t("Employment:Form.DateMustFitToEmploymentDuration"), false, testContext.path);
        }

        // otherwise it's valid
        return true;
    };
};

const hasClosedAttendance = (args: IGetValueArgs): boolean => {
    const storage = args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>;
    return isDefined(storage.getCustomData().lastClosedAttendanceDate);
};

const isForeigner: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const bc = storage.data.bindingContext
        .navigate(PrEmploymentEntity.Employee)
        .navigate(PrEmployeeEntity.CitizenshipCode);
    const countryCode = storage.getValue(bc);
    return !!countryCode && countryCode !== CountryCode.CzechRepublic;
};

export const EXPORT_CERTIFICATE_ACTION = "ExportEmploymentCertificate";

export const getDefinitions: IGetDefinition = (): IDefinition => {
    const table: ISplitPageTableDef = {
        id: `${EntityTypeName.PrEmployment}Table`,
        filterBarDef: [{
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            defaultFilters: [
                PrEmploymentEntity.Name,
                PrEmploymentEntity.Employee,
                PrEmploymentEntity.NumberOurs,
                PrEmploymentEntity.Type
            ],
            filterDefinition: {
                ...getEmployeeFilterDef(PrEmploymentEntity.Employee),
                [PrEmploymentEntity.Name]: {},
                [PrEmploymentEntity.NumberOurs]: {},
                [PrEmploymentEntity.Type]: {
                    fieldSettings: {
                        displayName: "Name"
                    }
                }
            },
            isValueHelp: true
        }],
        tabs: [
            {
                id: EmploymentStatus.Active,
                title: i18next.t("Employment:Table.Active")
            },
            {
                id: EmploymentStatus.Planned,
                title: i18next.t("Employment:Table.Planned")
            },
            {
                id: EmploymentStatus.Finished,
                title: i18next.t("Employment:Table.Finished")
            },
            {
                id: SECONDARY_FILTERS_ALL,
                title: i18next.t("Employment:Table.All"),
            },
        ],
        tabsSettings: {
            customFilter: getFilterForTabId
        },
        initialSortBy: [{ id: PrEmploymentEntity.NumberOurs, sort: Sort.Asc }],
        columns: [
            PrEmploymentEntity.NumberOurs,
            PrEmploymentEntity.Name,
            PrEmploymentEntity.Employee,
            createPath(PrEmploymentEntity.Employee, PrEmployeeEntity.NumberOurs),
            PrEmploymentEntity.Type,
            PrEmploymentEntity.DateStart,
            PrEmploymentEntity.DateEnd,
        ],
        columnDefinition: {
            ...getEmployeeTableColumnDefs(PrEmploymentEntity.Employee),
            [PrEmploymentEntity.NumberOurs]: {},
            [PrEmploymentEntity.Name]: {
                label: i18next.t("Employment:Table.PositionName")
            },
            [PrEmploymentEntity.Type]: {
                fieldSettings: {
                    displayName: "Name"
                }
            },
            [PrEmploymentEntity.DateStart]: {
                label: i18next.t("Employment:DateStart"),
            },
            [PrEmploymentEntity.DateEnd]: {
                label: i18next.t("Employment:DateEnd"),
            },
            [PrEmploymentEntity.DateCalculatePayslipStart]: {
                label: i18next.t("Employment:DateCalculatePayslipStart"),
                formatter: val => {
                    if (val) {
                        return capitalize(DateType.format(val as Date, DateFormat.monthAndYear));
                    }
                    return "";
                }
            },
        },
        title: i18next.t("Employment:Title"),
    };

    const getDefaultDateStart = (): Date => {
        return getUtcDayjs().startOf("month").toDate();
    };

    const summary: ISummaryItem[] = [
        { ...getNumberOursSummaryDef("Employment") },
        {
            id: BindingContext.localContext("Status"),
            additionalProperties: [{ id: PrEmploymentEntity.DateStart }, { id: PrEmploymentEntity.DateEnd }],
            label: i18next.t("Employment:Summary.Status"),
            isVisible: ({ storage }: IGetValueArgs) => {
                return !storage.data.bindingContext.isNew();
            },
            formatter: (_, { storage }) => {
                const entity = (storage as FormStorage).data.origEntity;
                const dateStart = getUtcDayjs(entity.DateStart);
                const dateEnd = getUtcDayjs(entity.DateEnd);
                const now = getUtcDayjs();

                if (dateStart.isAfter(now, "day")) {
                    return i18next.t("Employment:Table.Planned");
                } else if (dateEnd.isSameOrAfter(now, "day") || !dateEnd.isValid()) {
                    return i18next.t("Employment:Table.Active");
                } else {
                    return i18next.t("Employment:Table.Finished");
                }
            },
            colorFormatter: (_, { storage }) => {
                const entity = (storage as FormStorage).data.origEntity;
                const dateStart = getUtcDayjs(entity.DateStart);
                const dateEnd = getUtcDayjs(entity.DateEnd);
                const now = getUtcDayjs();
                if (now.isSameOrBefore(dateEnd) && now.isAfter(dateStart)) {
                    return "C_SEM_text_good";
                }
                return "";
            }
        },
        // {
        //     id: BindingContext.localContext("TemplateSummary"),
        //     additionalProperties: [{ id: PrEmploymentEntity.Template }],
        //     renderEdit: (args: ISummaryItemEditProps) => (
        //         <TemporalFormDialog storage={args.storage}
        //                             fieldPaths={[[PrEmploymentEntity.Template]]}
        //                             title={args.item.additionalData?.dialogTitle}
        //                             onChange={args.onChange}
        //                             onClose={args.onClose}
        //         />
        //     ),
        //     isReadOnly: ({ storage }: IGetValueArgs) => {
        //         const { entity, bindingContext } = storage.data;
        //         return !bindingContext.isNew() || !!(entity as IPrEmploymentEntity).Template?.Id;
        //     },
        //     label: i18next.t("Employment:Summary.Template"),
        //     additionalData: {
        //         dialogTitle: i18next.t("Employment:Summary.TemplateDialogTitle")
        //     },
        //     formatter(_, { entity, storage }) {
        //         if (entity.Template?.Name) {
        //             return (
        //                 <TemplateSummaryItem storage={storage as FormStorage<IPrEmploymentEntity>}/>
        //             );
        //         }
        //         return i18next.t("Employment:Summary.WithoutTemplate");
        //     },
        // }
    ];

    const submissionFormTabDef = getSubmissionTableDefinitionForFormTabs(createPath(PrSubmissionRegZecEntity.Employment, RegZecEmploymentEntity.Employment));
    submissionFormTabDef.table.entitySet = EntitySetName.PrSubmissionsRegZec;

    const form: IFormDef = {
        id: `${EntityTypeName.PrEmployment}Form`,
        translationFiles: getDefinitions.translationFiles,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("Employment:New"), i18next.t("Employment:FormTitle")),
        isDeletable: true,
        formControl: EmploymentFormView,
        summary,
        isReadOnly: ({ data }) => {
            return !isObjectEmpty((data as IPrEmploymentEntity).EmploymentClose);
        },
        customHeaderActions: [{
            id: PrSubmissionTypeCode.EmployeeRegistration,
            label: i18next.t("Employment:Form.CreateRegZec"),
            iconName: "REGZEC",
            ignoreMasterDisabled: true,
            isVisible: not(isNewEntity),
            isDisabled: false
        }, {
            id: EXPORT_CERTIFICATE_ACTION,
            label: i18next.t("Employment:Form.ExportCertificate"),
            iconName: "ExportPdf",
            ignoreMasterDisabled: true,
            isVisible: not(isNewEntity),
            isDisabled: (args) => {
                const empCloseEntity = args.storage.getValueByPath(PrEmploymentEntity.EmploymentClose);
                return isObjectEmpty(empCloseEntity);
            }
        }],
        additionalProperties: [
            ...getTemporalAdditionalProperties(PrEmploymentTemporalEntity, PrEmploymentEntity),
            ...NumberRangeAdditionalProperties,
            { id: PrEmploymentEntity.EmploymentClose },
            {
                id: PrEmploymentEntity.EmploymentClose
            }, {
                id: PrEmploymentEntity.ExtraSalaries,
                additionalProperties: [
                    {
                        id: PrEmploymentExtraSalaryEntity.Default,
                        additionalProperties: [
                            { id: PrExtraSalaryEntity.TypeCode },
                            {
                                id: PrExtraSalaryEntity.Type,
                                additionalProperties: [{ id: PrExtraSalaryTypeEntity.OriginCode }]
                            }
                        ]
                    }
                ]
            },
            {
                id: PrEmploymentEntity.Employee,
                additionalProperties: [
                    { id: PrEmployeeEntity.FirstName },
                    { id: PrEmployeeEntity.LastName },
                    { id: PrEmployeeEntity.CitizenshipCode },
                ]
            },
            {
                id: PrEmploymentEntity.InitialData,
                additionalProperties: [
                    { id: PrEmploymentInitialDataEntity.Year },
                    { id: PrEmploymentInitialDataEntity.AverageSalaryLastQuarter },
                    { id: PrEmploymentInitialDataEntity.DppWorkedFromLimitInMinutes },
                    { id: PrEmploymentInitialDataEntity.VacationTransferredFromLastYearInMinutes },
                    { id: PrEmploymentInitialDataEntity.VacationDrawdownThisYearInMinutes },
                    { id: PrEmploymentInitialDataEntity.FullyCountableTowardVacationInMinutes },
                    { id: PrEmploymentInitialDataEntity.PartiallyCountableTowardVacationInMinutes },
                    { id: PrEmploymentInitialDataEntity.SickDaysDrawdownThisYear },
                    { id: PrEmploymentInitialDataEntity.OvertimePreviousInMinutes },
                    { id: PrEmploymentInitialDataEntity.HolidayPreviousInMinutes },
                    {
                        id: PrEmploymentInitialDataEntity.EmploymentMonthData,
                        additionalProperties: [
                            { id: PrEmploymentInitialMonthDataEntity.DateMonth },
                            { id: PrEmploymentInitialMonthDataEntity.SocialSecurityInsuranceBasis },
                            { id: PrEmploymentInitialMonthDataEntity.ExcludedDays }
                        ]
                    },
                    {
                        id: PrEmploymentInitialDataEntity.LongTermTimeBonuses,
                        additionalProperties: [
                            { id: PrEmploymentInitialLongTermTimeBonusEntity.DateMonth },
                            { id: PrEmploymentInitialLongTermTimeBonusEntity.TimeBasedBonusTypeCode },
                            {
                                id: PrEmploymentInitialLongTermTimeBonusEntity.TimeBasedBonusType,
                                additionalProperties: [
                                    { id: PrTimeBasedBonusTypeEntity.Code },
                                    { id: PrTimeBasedBonusTypeEntity.Name }
                                ]
                            },
                            { id: PrEmploymentInitialLongTermTimeBonusEntity.Amount }
                        ]
                    }
                ]
            },
            {
                id: PrEmploymentEntity.SalaryComponents,
                additionalProperties: [
                    {
                        id: PrEmploymentSalaryComponentEntity.Default,
                        additionalProperties: [
                            { id: PrSalaryComponentEntity.TypeCode },
                            { id: PrSalaryComponentEntity.RegularSalaryTypeCode },
                        ]
                    }
                ]
            },
            {
                id: PrEmploymentEntity.Benefits,
                additionalProperties: [
                    {
                        id: PrEmploymentBenefitEntity.Default,
                        additionalProperties: [
                            { id: PrBenefitEntity.TypeCode }
                        ]
                    }
                ]
            }
        ],
        fieldDefinition: {
            ...getNumberRangeFieldDefs("Employment"),     // Number range fields: NumberOurs, NumberYours
            ...getCommonEmploymentDefs(false),              // Definitions common to Employment and Employment Template
            [PrEmploymentEntity.Employee]: {
                ...getEmployeeSelectDef([PrEmployeeEntity.CitizenshipCode]),
                affectedFields: [
                    { id: PrEmploymentEntity.MinimalEducation }
                ]
            },
            [PrEmploymentEntity.Type]: {
                type: FieldType.ComboBox,
                isRequired: true,
                isReadOnly: hasClosedAttendance,
                width: BasicInputSizes.L,
                defaultValue: PrEmploymentTypeCode.Employment,
                fieldSettings: {
                    displayName: "Name",
                    items: sortedEmploymentTypes.map(type => ({
                        id: type,
                        label: getEnumDisplayValue(EntityTypeName.PrEmploymentType, type)
                    })),
                    itemsForRender: (items, args) => {
                        if (args.storage.getValueByPath(PrEmploymentEntity.EmploymentCategory)?.Code !== PrEmploymentCategoryCode.Associate) {
                            return items?.filter(item => item.id !== PrEmploymentTypeCode.Compensation);
                        }
                        return items;
                    },
                },
                affectedFields: [
                    { id: PrEmploymentEntity.CsszEmploymentType },
                    { id: createPath(PrEmploymentEntity.CurrentTemporalPropertyBag, PrEmploymentTemporalCurrentEntity.SickDays) },
                    { id: createPath(PrEmploymentEntity.CurrentTemporalPropertyBag, PrEmploymentTemporalCurrentEntity.IsSmallScaleEmployment) }
                ]
            },
            [PrEmploymentEntity.EmploymentCategory]: {
                type: FieldType.ComboBox,
                defaultValue: PrEmploymentCategoryCode.Employee,
                isReadOnly: hasClosedAttendance,
                fieldSettings: {
                    displayName: "Name",
                },
                affectedFields: [
                    { id: PrEmploymentEntity.Type },
                    { id: PrEmploymentEntity.CsszEmploymentType }
                ]
            },
            [PrEmploymentEntity.CsszEmploymentType]: {
                ...getEmploymentTypeDef(PrEmploymentEntity.Type),
                defaultValue: CsszEmploymentTypeCode.FirstEmployment,
            },
            [PrEmploymentEntity.DateStart]: {
                type: FieldType.Date,
                label: i18next.t("Employment:DateStart"),
                groupedField: GroupedField.MultiStart,
                defaultValue: getDefaultDateStart,
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (_, args) => {
                            const storage = args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>;
                            const employment = storage.data.entity;
                            const validation = storage.getCustomData().initialDataEmploymentValidation;
                            return !validation?.shouldValidate
                                || validation.employeeId !== employment.Employee?.Id
                                || !hasCalculatedPayrollStartMonthConflict(employment, validation.hasCalculatedPayroll);
                        },
                        message: i18next.t("Employment:Form.CalculatedPayrollRequiresSameStartMonth")
                    }
                },
                affectedFields: [
                    { id: PrEmploymentEntity.DateCalculatePayslipStart, revalidate: true },
                    { id: PrEmploymentEntity.DateFirstDayAtWork, revalidate: true },
                    { id: PrEmploymentEntity.DateEnd, revalidate: true },
                ],
            },
            [PrEmploymentEntity.DateEnd]: {
                type: FieldType.Date,
                label: i18next.t("Employment:DateEnd"),
                groupedField: GroupedField.MultiEnd,
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutEnd
                },
                defaultValue: DATE_MAX,
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (value: TValue, args: IGetValueArgs, testContext): boolean | ValidationError => {
                            const { storage } = args;
                            const entity = storage.data.entity;
                            const { DateStart } = entity;
                            const DateEnd = getUtcDayjs(value as TDateLike);
                            // if date start is set and later than the date calculate payslip start
                            if (DateEnd && DateStart && !DateEnd.isSameOrAfter(DateStart, "day")) {
                                return new ValidationError(i18next.t("Employment:Form.CalculationPayslipEndBeforeStart"), false, testContext.path);
                            }

                            // otherwise it's valid
                            return true;
                        }
                    }
                },
                affectedFields: [
                    { id: PrEmploymentEntity.DateCalculatePayslipStart, revalidate: true },
                    { id: PrEmploymentEntity.DateFirstDayAtWork, revalidate: true }
                ]
            },
            [PrEmploymentEntity.DateCalculatePayslipStart]: {
                type: FieldType.MonthYear,
                isRequired: true,
                label: i18next.t("Employment:DateCalculatePayslipStart"),
                customFieldStyle: { marginRight: "8px" },
                defaultValue: getUtcDayjs().startOf("month").toDate(),
                isDisabled: (args) => {
                    const { storage } = args;
                    return isDefined((storage.getCustomData() as IEmploymentCustomData).lastClosedAttendanceDate);
                },
                affectedFields: [
                    { id: PrEmploymentEntity.DateStart, revalidate: true },
                    { id: EmploymentInitialDataActionPath }
                ],
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: [{
                            validator: getFitToEmploymentDurationValidator("month")
                        }, {
                            validator: (_, args) => {
                                if (isBeforeLastClosedPayrollPeriod(args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>, true)) {
                                    return true;
                                }
                                return !isBeforeLastClosedPayrollPeriod(args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>);
                            },
                            message: i18next.t("Employment:Form.PrPeriodValidation")
                        }, {
                            validator: (_, args) => {
                                const storage = args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>;
                                const employment = storage.data.entity;
                                const validation = storage.getCustomData().initialDataEmploymentValidation;
                                return !validation?.shouldValidate
                                    || validation.employeeId !== employment.Employee?.Id
                                    || !hasInitialDataPayrollMonthConflict(employment, validation.otherEmployments);
                            },
                            message: i18next.t("Employment:Form.InitialDataFirstPayrollMonthMismatch")
                        }]
                    }
                },
                customizationData: {
                    dependents: [EmploymentInitialDataActionPath]
                }
            },
            [EmploymentInitialDataActionPath]: {
                type: FieldType.Custom,
                useForValidation: true,
                label: i18next.t("Employment:Form.InitialDataAction"),
                isVisible: args => isInitialDataRelevantEmployment(args.storage.data.entity as IPrEmploymentEntity),
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: (_, args) => {
                            const employment = args.storage.data.entity as IPrEmploymentEntity;
                            return !isInitialDataRelevantEmployment(employment) || !!employment.InitialData;
                        },
                        message: i18next.t("Error:PrEmploymentInitialDataRequired")
                    }
                },
                render: args => {
                    const error = args.props?.error;
                    const storage = args.storage as FormStorage<IPrEmploymentEntity, IEmploymentCustomData>;
                    const openInitialData = () => {
                        storage.setCustomData({ initialDataDialogOpen: true });
                        storage.refresh();
                    };

                    return <div style={{ alignSelf: "flex-end" }}>
                        <IconButton
                            isTransparent
                            onClick={openInitialData}
                            error={error}
                            title={i18next.t("Employee:InitialData.Load")}>
                            <OldImportClockIcon/>
                        </IconButton>
                    </div>;
                }
            },
            ...getAddressFields(PrEmploymentEntity.WorkplaceAddress, {
                isReadOnly: hasNotOwnWorkplaceAddress
            }),
            [PrEmploymentEntity.WorkplaceAddressSelection]: {
                type: FieldType.ComboBox,
                label: i18next.t("Employment:Form.WorkplaceAddressSelection"),
                defaultValue: PrEmploymentWorkplaceAddressSelectionCode.CompanyDefault,
                fieldSettings: {
                    displayName: PrEmploymentWorkplaceAddressSelectionEntity.Name
                },
                collapsedRows: [
                    ...getAddressGroup(PrEmploymentEntity.WorkplaceAddress)
                ]
            },
            [PrEmploymentEntity.Name]: {
                width: BasicInputSizes.XL,
                label: i18next.t("Employment:Form.PositionName")
            },
            [PrEmploymentEntity.Template]: {
                type: FieldType.ComboBox,
                label: i18next.t("Employment:Template.TitleSingular"),
                width: BasicInputSizes.L,
                fieldSettings: {
                    displayName: PrEmploymentTemplateEntity.Name,
                    shouldDisplayAdditionalColumns: true,
                },
                columns: [{
                    id: PrEmploymentTemplateEntity.Name
                }, {
                    id: PrEmploymentTemplateEntity.NumberOurs,
                }],
            },
            // ------- Properties for REGZEC -------
            [PrEmploymentEntity.MpsvIdentifier]: {
                width: BasicInputSizes.L,
            },
            ...getTemporalPropertyFieldDefinition({
                propName: PrEmploymentTemporalCurrentEntity.IsSmallScaleEmployment,
                definition: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Employment:Form.IsSmallScaleEmployment"),
                    width: BasicInputSizes.S,
                    isVisible: (args) => !isDPP(args) && !isStatutoryWithCompensation(args),
                    defaultValue: false,
                    useForValidation: true,
                    fieldSettings: {
                        temporalDialog: {
                            dialogTitle: i18next.t("Employment:Form.IsSmallScaleEmploymentDialogTitle"),
                            granularity: "month",
                            withoutGaps: true,
                            cannotBeEnded: true
                        },
                        items: getSimpleBoolSelectItems()
                    },
                }
            }),
            [PrEmploymentEntity.Isco]: {
                ...IscoDef,
                width: BasicInputSizes.L,
                // defaultValue: CzIscoCode., todo: default should show all columns
            },
            [PrEmploymentEntity.IsManagerialEmployee]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
            },
            [PrEmploymentEntity.DetailedSpecification]: {
                ...detailedSpecificationDef,
                label: i18next.t("Employment:Form.DetailedSpecification"),
                width: BasicInputSizes.L,
                defaultValue: DetailedEmploymentSpecificationCode.None,
                fieldSettings: {
                    ...detailedSpecificationDef.fieldSettings,
                }
            },
            [PrEmploymentEntity.DateFirstDayAtWork]: {
                type: FieldType.Date,
                label: i18next.t("Employment:Form.DateFirstDayAtWork"),
                defaultValue: getDefaultDateStart,
                validator: {
                    type: ValidatorType.Custom,
                    settings: {
                        customValidator: getFitToEmploymentDurationValidator("day")
                    }
                }
            },
            [PrEmploymentEntity.MinimalEducation]: {
                ...educationDef,
                label: i18next.t("Employment:Form.MinimalEducation"),
                isRequired: true,
                isVisible: isForeigner
            },
            // ------- Properties for REGZEC (Statistical data) -------
            [createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.Isce)]: {
                ...IsceDef,
                width: BasicInputSizes.L,
                isRequired: true,
                // defaultValue: CzIsceCode.CZISCE1, todo: default should show all columns
            },
            [createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.ShiftPattern)]: {
                ...shiftPatternDef,
                defaultValue: ShiftPatternCode.Single,
            },
            [createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.IsContinuesOperation)]: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                },
            },
            [createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.WorkplaceType)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.L,
                defaultValue: WorkplaceTypeCode.OnPrem,
                fieldSettings: {
                    displayName: WorkplaceTypeEntity.Name,
                    noRecordText: i18next.t("Common:Select.NoRecordUnspecified"),
                    orderBy: {
                        propertyName: WorkplaceTypeEntity.MpsvCode,
                        asc: true
                    }
                }
            },
            [createPath(PrEmploymentEntity.Cars, PrEmploymentCarEntity.Name)]: {},
            [createPath(PrEmploymentEntity.Cars, PrEmploymentCarEntity.PurchasePrice)]: {
                ...getPayrollCurrencyFieldDef()
            },
            [createPath(PrEmploymentEntity.Cars, PrEmploymentCarEntity.Taxability)]: {
                type: FieldType.ComboBox,
                width: BasicInputSizes.S,
                fieldSettings: {
                    displayName: PrEmployeeCarTaxabilityEntity.Name
                }
            },
            [createPath(PrEmploymentEntity.Cars, PrEmploymentCarEntity.DateValidFrom)]: {
                defaultValue: () => getUtcDayjs(),
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutStart
                }
            },
            [createPath(PrEmploymentEntity.Cars, PrEmploymentCarEntity.DateValidTo)]: {
                defaultValue: () => DATE_MAX,
                fieldSettings: {
                    showSpecialValue: DateRangeSpecialValue.WithoutEnd
                }
            },
            // ----- Retirement savings -----
            ...getRetirementSavingsBasicFields(),
            ...getTemporalPropertyLineItemFieldDefinition({
                collectionName: PrEmploymentEntity.RetirementSavings,
                propName: PrEmploymentRetirementSavingTemporalCurrentEntity.ComputationType,
                definition: getRetirementSavingsComputationTypeDef()
            }),
            ...getTemporalPropertyLineItemFieldDefinition({
                collectionName: PrEmploymentEntity.RetirementSavings,
                propName: PrEmploymentRetirementSavingTemporalCurrentEntity.IsIncludedAutomatically,
                definition: getRetirementSavingsIsIncludedAutomaticallyDef()
            }),
            ...Object.entries(getRetirementSavingsSecondaryFields()).reduce(
                (acc, [key, def]) => ({
                    ...acc,
                    ...getTemporalPropertyLineItemFieldDefinition({
                        collectionName: PrEmploymentEntity.RetirementSavings,
                        propName: key,
                        definition: {
                            ...def,
                            fieldSettings: {
                                ...getRetirementSavingsTemporalDialogSetting(),
                                ...def.fieldSettings
                            }
                        },
                        withOpeningButton: key === PrEmploymentRetirementSavingTemporalCurrentEntity.RemittanceInformation
                    })
                }),
                {} as TFieldsDefinition
            ),
            // ----- /Retirement savings -----
        },
        groups: [
            {
                id: "main",
                rows: [[
                    { id: PrEmploymentEntity.Name },
                    { id: PrEmploymentEntity.Employee },
                    { id: PrEmploymentEntity.MpsvIdentifier },
                    { id: PrEmploymentEntity.EmploymentCategory },
                    { id: PrEmploymentEntity.Type },
                    { id: PrEmploymentEntity.CsszEmploymentType },
                    { id: createPath(PrEmploymentEntity.CurrentTemporalPropertyBag, PrEmploymentTemporalCurrentEntity.IsSmallScaleEmployment) },
                    { id: PrEmploymentEntity.WorkplaceAddressSelection },
                ]]
            },
            {
                id: "dates",
                title: i18next.t("Employment:Form.DatesGroupTitle"),
                rows: [[
                    { id: PrEmploymentEntity.DateStart },
                    { id: PrEmploymentEntity.DateEnd },
                    { id: PrEmploymentEntity.DateFirstDayAtWork },
                    { id: PrEmploymentEntity.DateCalculatePayslipStart },
                    { id: EmploymentInitialDataActionPath },
                ]]
            },
            {
                id: "detail",
                title: i18next.t("Employment:Form.DetailGroupTitle"),
                rows: [[
                    { id: PrEmploymentEntity.Isco },
                    { id: PrEmploymentEntity.IsManagerialEmployee },
                    { id: PrEmploymentEntity.MinimalEducation },
                    { id: PrEmploymentEntity.DetailedSpecification },
                    { id: createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.Isce) },
                    { id: createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.WorkplaceType) },
                    { id: createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.ShiftPattern) },
                    { id: createPath(PrEmploymentEntity.StatisticalData, PrStatisticalDataEntity.IsContinuesOperation) },
                ]]
            },
            ...getCommonEmploymentGroupDef(false),
            {
                id: "Tabs",
                isVisible: not(isNewEntity),
                tabs: [
                    submissionFormTabDef
                ]
            }
        ]
    };

    return {
        entitySet: EntitySetName.PrEmployments,
        table,
        form
    };
};
getDefinitions.translationFiles = [
    ...EmploymentTranslationFiles,
    getEnumNameSpaceName(EntityTypeName.PrEmploymentWorkplaceAddressSelection),
    getEnumNameSpaceName(EntityTypeName.PrEmploymentType)
];
setDefByEntityType(EntityTypeName.PrEmployment, getDefinitions);
